// The warehouse query card's data shapes. They mirror the `warehouse_query` event the tool
// plugin emits (agent-runner/tools/plugins/warehouse_query.ts) and the two
// browser routes under /agent/:customer/session/:sessionId/warehouse.

import type { WarehouseColumn, WarehouseQueryResult, WarehouseSavedQuery } from '../../utils/warehouseConnections'

// The column, result and saved-query shapes are the admin page's: one
// definition for both.
export type { WarehouseColumn, WarehouseQueryResult, WarehouseSavedQuery }

export type WarehouseRow = Record<string, unknown>

/** The `warehouse_query` event body, plus the harness's id and tool call id. */
export interface WarehouseQueryEvent {
  id?: string
  toolCallId?: string
  connection_id: string
  connection_name: string
  connection_type: string
  title: string
  sql: string
  columns: WarehouseColumn[]
  /** The first 50 rows. The full result is in saved_path, when there is one. */
  rows: WarehouseRow[]
  row_count: number
  truncated: boolean
  bytes_processed: number
  duration_ms: number
  saved_path: string
  query_log_id: string
  /** Set when the query failed: the card shows it instead of a grid. */
  error: string
}

/** What the card draws: the event at first, then whatever the last Run returned. */
export interface WarehouseResultView {
  columns: WarehouseColumn[]
  rows: WarehouseRow[]
  row_count: number
  truncated: boolean
  bytes_processed: number
  duration_ms: number
  error: string
}

export const PREVIEW_ROWS = 50

export const viewFromEvent = (e: WarehouseQueryEvent): WarehouseResultView => ({
  columns: e.columns,
  rows: e.rows,
  row_count: e.row_count,
  truncated: e.truncated,
  bytes_processed: e.bytes_processed,
  duration_ms: e.duration_ms,
  error: e.error,
})

export const viewFromResult = (r: WarehouseQueryResult): WarehouseResultView => ({
  columns: r.columns ?? [],
  rows: (r.rows ?? []).slice(0, PREVIEW_ROWS),
  row_count: r.row_count ?? (r.rows ?? []).length,
  truncated: !!r.truncated,
  bytes_processed: r.bytes_processed ?? 0,
  duration_ms: r.duration_ms ?? 0,
  error: '',
})

/** The message "Use this version" puts in the composer. */
export const changedQueryMessage = (connectionName: string, sql: string): string =>
  `I changed the query on ${connectionName} to:\n\n\`\`\`sql\n${sql.trim()}\n\`\`\`\n`

const str = (v: unknown) => (typeof v === 'string' ? v : '')
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0)

/** A `warehouse_query` event's payload as the card reads it; null without a connection. */
export function warehouseEventFromData(d: Record<string, unknown>, id: string | undefined): WarehouseQueryEvent | null {
  if (!d.connection_id) return null
  return {
    id,
    toolCallId: str(d.toolCallId) || undefined,
    connection_id: str(d.connection_id),
    connection_name: str(d.connection_name),
    connection_type: str(d.connection_type),
    title: str(d.title),
    sql: str(d.sql),
    columns: Array.isArray(d.columns) ? (d.columns as WarehouseQueryEvent['columns']) : [],
    rows: Array.isArray(d.rows) ? (d.rows as WarehouseQueryEvent['rows']) : [],
    row_count: num(d.row_count),
    truncated: !!d.truncated,
    bytes_processed: num(d.bytes_processed),
    duration_ms: num(d.duration_ms),
    saved_path: str(d.saved_path),
    query_log_id: str(d.query_log_id),
    error: str(d.error),
  }
}
